import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
} from '@angular/core';

@Component({
    selector: 'maps-things-to-look-into-box',
    standalone: false,
    template: `
        <section class="box" [class.is-expanded]="expanded" aria-label="Things to look into">
            <header>Things to Look Into</header>
            <!-- Deterministic JD-constraint notes (structured-fit layer) lead
                 the box, regular weight (Test Results 7-15-26), ahead of the
                 AI gap text. -->
            <ul class="flags" *ngIf="flags?.length">
                <li *ngFor="let f of flags">{{ f }}</li>
            </ul>
            <p
                *ngIf="text; else skeleton"
                #textEl
                class="text"
                [class.text--expanded]="expanded || full">
                {{ text }}
            </p>
            <button
                *ngIf="!full && text && (isClamped || expanded)"
                type="button"
                class="toggle"
                [class.toggle--collapsed]="!expanded"
                [attr.aria-expanded]="expanded"
                (click)="expanded = !expanded">
                {{ expanded ? 'Show less' : 'Show more' }}
            </button>
            <ng-template #skeleton>
                <!-- Failure beats shimmer: an indefinite skeleton after a dead
                     request reads as a hung page (stage incident 2026-07-13). -->
                <ng-container *ngIf="failed; else generating">
                    <p class="unavailable">
                        Summary unavailable —
                        <button type="button" class="retry" (click)="retry.emit()">Retry</button>
                    </p>
                </ng-container>
                <ng-template #generating>
                    <div class="skeleton" aria-hidden="true">
                        <span class="line"></span>
                        <span class="line short"></span>
                    </div>
                    <span class="sr-only">Generating summary…</span>
                </ng-template>
            </ng-template>
        </section>
    `,
    styleUrl: './things-to-look-into-box.component.scss',
})
export class ThingsToLookIntoBoxComponent implements AfterViewChecked {
    @Input() text: string | null | undefined = null;
    /** Deterministic fit flags rendered bold ahead of the AI gap text. */
    @Input() flags: string[] | null | undefined = null;
    /** True when summary generation gave up (retries exhausted): renders the
     *  retry affordance instead of an indefinite skeleton. */
    @Input() failed = false;
    /** Drawer mode: text renders unclamped with no toggle. Since the STN
     *  round this box appears ONLY in the experience drawer (it left the
     *  card per the client mockup). */
    @Input() full = false;
    @Output() retry = new EventEmitter<void>();
    @ViewChild('textEl') textEl?: ElementRef<HTMLElement>;

    expanded = false;
    isClamped = false;

    private measuredFor: string | null | undefined = undefined;

    constructor(private readonly cdr: ChangeDetectorRef) {}

    /**
     * Detect whether the clamped text actually overflows, so the "Show more"
     * affordance only appears when there's hidden content. Re-measures when the
     * text input changes (it arrives async, replacing the skeleton).
     */
    ngAfterViewChecked(): void {
        if (this.expanded || this.measuredFor === this.text) {
            return;
        }
        this.measuredFor = this.text;
        const el = this.textEl?.nativeElement;
        const clamped = el ? el.scrollHeight > el.clientHeight + 1 : false;
        if (clamped !== this.isClamped) {
            this.isClamped = clamped;
            this.cdr.detectChanges();
        }
    }
}
